import type { UseModalProps } from '@chakra-ui/react'
import { sort } from 'radash'
import { immer } from 'zustand/middleware/immer'
import { createWithEqualityFn as create } from 'zustand/traditional'
import { shallow } from 'zustand/vanilla/shallow'

import { createSelectors } from '@/shared/lib/createSelectors.ts'

import { ID_DIVIDER } from '../config/idDivider.ts'
import { wwtModalManagerLog } from '../logger/wwtModalManagerLog.ts'
import {
	ModalInfo,
	ModalPriority,
	ModalProps,
	ModalTypes
} from '../types/modalTypes.ts'
import { generateNewModalInfo } from '../utils/generateNewModalInfo.ts'
import { getChildModalsInfoFromModalTree } from '../utils/getChildModalsInfoFromModalTree.ts'

export interface ModalManagerState {
	/**
	 * Modals array for rendering
	 *
	 * At any given time, only one modal with the lowest index is shown
	 *
	 * When adding a new modal, we sort by priority, with higher priority modals
	 * having lower indices
	 */
	modals: ModalInfo<ModalTypes>[]
}

export interface ModalManagerActions {
	/**
	 * Method uses for show new modal
	 *
	 * If modal already in the modal list - **we show duplicate**
	 *
	 * @param type - required, type of modal
	 * @param props - props for pass to modal
	 * @param priority - priority of modal, default is `default`
	 * @param modalProps - props for pass to modal component
	 */
	showModal: <T extends ModalTypes>(
		type: T,
		props?: ModalProps[T],
		priority?: ModalPriority,
		modalProps?: Partial<UseModalProps>
	) => string
	/**
	 * Method uses for show child modal
	 *
	 * @param patentModalId - id of parent modal
	 *
	 * Other same with `showModal`
	 */
	showChildModal: <T extends ModalTypes>(
		parentModalId: string,
		type: T,
		props?: ModalProps[T],
		priority?: ModalPriority,
		modalProps?: Partial<UseModalProps>
	) => void
	/**
	 * Method for close modal on any level
	 *
	 * @param id - Id of modal for close
	 */
	closeModal: (id: string) => void
}

export const useModalManagerBase = create<
	ModalManagerState & ModalManagerActions
>()(
	immer(set => ({
		modals: [],

		showModal: (type, props, priority = ModalPriority.default, modalProps) => {
			let modalId: string | null = null

			set(state => {
				const newModalInfo = generateNewModalInfo(
					type,
					props,
					priority,
					modalProps
				)

				modalId = newModalInfo.id

				wwtModalManagerLog(`Showed modal with id = ${modalId}`)

				state.modals = sort(
					[...state.modals, newModalInfo],
					modal => modal.priority,
					true
				)
			})

			if (modalId === null) {
				throw new Error('Error on open modal, return id is null')
			}

			return modalId
		},

		showChildModal: (
			parentModalId,
			type,
			props,
			priority = ModalPriority.default,
			modalProps
		) => {
			let modalId: string | null = null

			set(state => {
				wwtModalManagerLog(
					`Show modal for parent = ${parentModalId}, with`,
					type,
					props,
					priority,
					modalProps
				)

				const newModalInfo = generateNewModalInfo(
					type,
					props,
					priority,
					modalProps,
					parentModalId + ID_DIVIDER
				)

				modalId = newModalInfo.id

				if (parentModalId.includes(ID_DIVIDER)) {
					wwtModalManagerLog('Get parent modal for show modal')

					const ids = parentModalId.split(ID_DIVIDER)

					const modalForChange = getChildModalsInfoFromModalTree(
						state.modals,
						ids,
						1
					)

					const parentModal = modalForChange.find(modal =>
						modal.id.includes(ids[ids.length - 1])
					)

					if (parentModal === undefined) {
						wwtModalManagerLog(
							`${
								ids[ids.length - 1]
							} not found in getModalInfoFromModalTree result`,
							JSON.stringify(modalForChange),
							JSON.stringify(state.modals)
						)
						throw new Error(
							`${
								ids[ids.length - 1]
							} not found in getModalInfoFromModalTree result`
						)
					}

					parentModal.childModals = sort(
						[...parentModal.childModals, newModalInfo],
						modal => modal.priority,
						true
					)

					wwtModalManagerLog(
						`Created child modal with id = ${modalId} for parent = ${parentModal.id}`
					)

					return
				}

				const parentModalIndex = state.modals.findIndex(
					modal => modal.id === parentModalId
				)

				if (parentModalIndex === -1) {
					throw new Error(
						'We cannot add child modal to not exists parent modal'
					)
				}

				state.modals[parentModalIndex].childModals = sort(
					[...state.modals[parentModalIndex].childModals, newModalInfo],
					modal => modal.priority,
					true
				)

				wwtModalManagerLog(
					`Created child modal with id = ${modalId} for parent = ${parentModalIndex}`
				)
			})

			return modalId
		},

		closeModal: id =>
			set(state => {
				wwtModalManagerLog(`Close modal for ${id}`)

				if (!id.includes(ID_DIVIDER)) {
					wwtModalManagerLog(
						'Close top-level modal, modals = ',
						JSON.stringify(state.modals)
					)

					state.modals = state.modals.filter(modal => modal.id !== id)
					return
				}

				const ids = id.split(ID_DIVIDER)

				wwtModalManagerLog(
					'Close one of child modal, modals = ',
					JSON.stringify(state.modals),
					' ids = ',
					ids
				)

				const modalForChange = getChildModalsInfoFromModalTree(
					state.modals,
					ids,
					1
				)

				const indexToRemove = modalForChange.findIndex(modal =>
					modal.id.includes(ids[ids.length - 1])
				)

				if (indexToRemove === -1) {
					wwtModalManagerLog(
						`${
							ids[ids.length - 1]
						} not found in getModalInfoFromModalTree result`,
						JSON.stringify(modalForChange),
						JSON.stringify(state.modals)
					)
					throw new Error(
						`${
							ids[ids.length - 1]
						} not found in getModalInfoFromModalTree result`
					)
				}

				modalForChange.splice(indexToRemove, 1)
			})
	})),
	shallow
)

export const useModalManager = createSelectors(useModalManagerBase)
